import type { CancelTokenSource } from 'axios';
import axios from 'axios';
import type { ComputedRef } from 'vue';
import type { ITicketError, Ticket } from '@/interface/ITicket';
import { PriceChangeStatus, TicketType } from '@/interface/enum';
import type { IVoucher } from '@/interface/voucher';
import type { IOpenTicketResult } from '@/libraries/apis/ticketApi';
import { openTicket as openTicketApi } from '@/libraries/apis/ticketApi';
import type { KeyedTimers } from '@/libraries/betSlip/keyedTimers';
import {
    GENERAL_FAILURE_MESSAGE_KEY,
    OpenTicketFailureAction,
    buildOpenTicketError,
    resolveOpenTicketFailure,
    resolveTicketChangeError,
} from '@/libraries/betSlip/singleModeBetSlipErrorMessage';
import {
    assertNever,
    getTicketPrice,
    validateTicketStake,
} from '@/libraries/betSlip/ticketHelper';
import { resolvePriceChangeStatus } from '@/libraries/composables/usePriceChange';

type FailedOpenTicketHandler = (
    ticket: Ticket,
    error: ITicketError,
) => void;

export interface ISingleModeOpenTicketDeps {
    tickets: ComputedRef<Ticket[]>;
    getTicket: (key: string) => Ticket | undefined;
    removalTimers: KeyedTimers;
    openTicketErrorTimers: KeyedTimers;
    openTicketErrorDelayMs: number;
    lockAndScheduleRemoval: (key: string) => void;
    applyVoucherResult: (ticket: Ticket, rawVouchers: IVoucher[] | null) => void;
    setBalance: (balance: IOpenTicketResult['balance']) => void;
}

export function useSingleModeOpenTicket(deps: ISingleModeOpenTicketDeps) {
    const {
        tickets,
        getTicket,
        removalTimers,
        openTicketErrorTimers,
        openTicketErrorDelayMs,
        lockAndScheduleRemoval,
        applyVoucherResult,
        setBalance,
    } = deps;
    const openTicketCancelTokens = new Map<string, CancelTokenSource>();

    async function openTicket(key: string, isFirstOpen = false) {
        const ticket = getTicket(key);
        if (!ticket) {
            return;
        }
        openTicketCancelTokens.get(key)?.cancel('Invalid previous Single mode openTicket');
        const cancelTokenSource = axios.CancelToken.source();
        openTicketCancelTokens.set(key, cancelTokenSource);
        ticket.isOpeningTicket = true;

        try {
            const result = await openTicketApi(ticket, cancelTokenSource.token);
            const currentTicket = getTicket(key);
            if (!currentTicket) {
                return;
            }
            applyOpenTicketResult(currentTicket, result, isFirstOpen);
            currentTicket.isOpeningTicket = false;
        } catch (error) {
            if (axios.isCancel(error)) {
                return;
            }
            const currentTicket = getTicket(key);
            if (!currentTicket) {
                return;
            }
            showTemporaryOpenTicketError(
                currentTicket,
                buildOpenTicketError(GENERAL_FAILURE_MESSAGE_KEY),
            );
            currentTicket.isOpeningTicket = false;
        } finally {
            if (openTicketCancelTokens.get(key) === cancelTokenSource) {
                openTicketCancelTokens.delete(key);
            }
        }
    }

    async function refreshTicket(key: string) {
        await openTicket(key);
    }

    async function refreshAllTickets() {
        const refreshableKeys = tickets.value
            .filter(ticket => !ticket.isPendingRemoval && !ticket.isPlacingBet)
            .map(ticket => ticket.key);
        await Promise.all(refreshableKeys.map(key => refreshTicket(key)));
    }

    async function openAllTicketsForSingleTab() {
        const openableTickets = tickets.value
            .filter(ticket => !ticket.isPendingRemoval && !ticket.isPlacingBet);
        await Promise.all(openableTickets.map(ticket => (
            openTicket(ticket.key, ticket.uid === '')
        )));
    }

    function cancelOpenTicket(key: string) {
        openTicketCancelTokens.get(key)?.cancel('Invalid previous Single mode openTicket');
        openTicketCancelTokens.delete(key);
    }

    function cancelAllOpenTickets(reason: string) {
        openTicketCancelTokens.forEach(cancelTokenSource => cancelTokenSource.cancel(reason));
        openTicketCancelTokens.clear();
    }

    function markTicketClosed(key: string, messageKey: string) {
        const ticket = getTicket(key);
        if (!ticket) {
            return;
        }
        removeFailedOpenTicket(ticket, buildOpenTicketError(messageKey));
    }

    function showTemporaryOpenTicketError(ticket: Ticket, error: ITicketError) {
        ticket.openTicketError = error;
        scheduleClearOpenTicketError(ticket.key);
    }

    function showPersistentOpenTicketError(ticket: Ticket, error: ITicketError) {
        openTicketErrorTimers.clear(ticket.key);
        ticket.openTicketError = error;
    }

    function scheduleClearOpenTicketError(key: string) {
        openTicketErrorTimers.schedule(key, openTicketErrorDelayMs, () => {
            const ticket = getTicket(key);
            if (ticket) {
                ticket.openTicketError = null;
            }
        });
    }

    function removeFailedOpenTicket(ticket: Ticket, error: ITicketError) {
        ticket.openTicketError = error;
        lockAndScheduleRemoval(ticket.key);
    }

    const failedOpenTicketHandlerMap: Record<OpenTicketFailureAction, FailedOpenTicketHandler> = {
        [OpenTicketFailureAction.Remove]: removeFailedOpenTicket,
        [OpenTicketFailureAction.ShowTemporaryError]: showTemporaryOpenTicketError,
        [OpenTicketFailureAction.ShowPersistentError]: showPersistentOpenTicketError,
    };

    function routeFailedOpenTicket(result: IOpenTicketResult, ticket: Ticket) {
        const { messageKey, action } = resolveOpenTicketFailure(result.errorStatus);
        failedOpenTicketHandlerMap[action](
            ticket,
            buildOpenTicketError(messageKey, result.errorStatus),
        );
    }

    function applyOpenTicketResult(
        ticket: Ticket,
        result: IOpenTicketResult,
        isFirstOpen = false,
    ) {
        ticket.uid = result.uid;

        if (!result.isSuccess) {
            routeFailedOpenTicket(result, ticket);
            return;
        }

        setBalance(result.balance);

        ticket.minBet = result.minBet;
        ticket.maxBet = result.maxBet;
        ticket.stakeError = validateTicketStake(ticket);
        removalTimers.clear(ticket.key);
        openTicketErrorTimers.clear(ticket.key);
        ticket.openTicketError = null;
        ticket.isPendingRemoval = false;
        if (!isFirstOpen) {
            applyOddsChangeResult(ticket, result);
            applyTicketChangeMessage(ticket, result);
        }
        applyTypeSpecificOpenTicketResult(ticket, result);
        applyVoucherResult(ticket, result.rawVouchers);
    }

    function applyOddsChangeResult(
        ticket: Ticket,
        result: IOpenTicketResult,
    ) {
        const oddsChangeStatus = resolvePriceChangeStatus(
            {
                value: result.price,
                priceStyle: result.priceStyle,
            },
            {
                value: getTicketPrice(ticket),
                priceStyle: ticket.priceStyle,
            },
        );
        if (oddsChangeStatus === PriceChangeStatus.Remain) {
            return;
        }
        ticket.oddsChangeStatus = oddsChangeStatus;
        ticket.isOddsChangeAccepted = false;
    }

    function applyTicketChangeMessage(
        ticket: Ticket,
        result: IOpenTicketResult,
    ) {
        if (ticket.type !== TicketType.Single) {
            return;
        }
        const changeError = resolveTicketChangeError(
            {
                point: ticket.odds.point,
                homeScore: ticket.odds.eventResult.liveHomeScore,
                awayScore: ticket.odds.eventResult.liveAwayScore,
            },
            {
                point: result.point,
                homeScore: result.score.home,
                awayScore: result.score.away,
            },
        );
        if (changeError === null) {
            return;
        }
        showTemporaryOpenTicketError(ticket, changeError);
    }

    function applyTypeSpecificOpenTicketResult(
        ticket: Ticket,
        result: IOpenTicketResult,
    ) {
        switch (ticket.type) {
            case TicketType.Single:
                ticket.price = result.price;
                ticket.priceStyle = result.priceStyle;
                ticket.odds.point = result.point;
                ticket.odds.betCondition = result.betCondition ?? '';
                ticket.odds.eventResult.liveHomeScore = result.score.home;
                ticket.odds.eventResult.liveAwayScore = result.score.away;
                return;
            case TicketType.DynamicMarket:
                ticket.selection.price = {
                    value: result.price,
                    priceStyle: result.priceStyle,
                };
                ticket.selection.score = {
                    home: result.score.home,
                    away: result.score.away,
                };
                ticket.selection.betCondition = result.betCondition;
                ticket.priceStyle = result.priceStyle;
                return;
            case TicketType.Outright:
                ticket.price = result.price;
                ticket.priceStyle = result.priceStyle;
                return;
            default:
                assertNever(ticket);
        }
    }

    return {
        openTicket,
        refreshTicket,
        refreshAllTickets,
        openAllTicketsForSingleTab,
        cancelOpenTicket,
        cancelAllOpenTickets,
        markTicketClosed,
        showTemporaryOpenTicketError,
    };
}
